// Contract configuration for Bet4Peer V2 - MAINNET with UMA Oracle
// Deployed on Base, Arbitrum, and Polygon with same address

// V2 Contract Address (same on all chains thanks to deterministic deployment)
export const CONTRACT_ADDRESS_V2 = '0x6101195b75557ff654E7DB12E2e1d7Ac3B8260FA';

// Legacy V1 Contract (for reference/migration)
export const CONTRACT_ADDRESS_V1 = '0x4871B27918B9a277b05F71455EF2597A27AAD6E2';

// Use V2 as the primary contract
export const CONTRACT_ADDRESS = CONTRACT_ADDRESS_V2;
export const CHAIN_ID = 8453; // Base Mainnet (primary)

// Multi-chain V2 contract addresses (same on all chains!)
export const CONTRACT_ADDRESSES = {
  8453: CONTRACT_ADDRESS_V2,   // Base Mainnet
  42161: CONTRACT_ADDRESS_V2,  // Arbitrum One
  137: CONTRACT_ADDRESS_V2,    // Polygon
};

// UMA Oracle addresses per chain
export const UMA_ORACLE_ADDRESSES = {
  8453: '0x2aBf1Bd76655de80eDB3086114315Eec75AF500c',   // Base
  42161: '0xa6147867264374F324524E30C02C331cF28aa879', // Arbitrum
  137: '0x5953f2538F613E05bAED8A5AeFa8e6622467AD3D',   // Polygon
};

// WETH addresses per chain (bond currency for oracle)
export const WETH_ADDRESSES = {
  8453: '0x4200000000000000000000000000000000000006',   // Base
  42161: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1', // Arbitrum
  137: '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619',   // Polygon
};

// Supported chains
export const SUPPORTED_CHAINS = {
  8453: { name: 'Base', symbol: 'ETH', explorer: 'https://basescan.org' },
  42161: { name: 'Arbitrum', symbol: 'ETH', explorer: 'https://arbiscan.io' },
  137: { name: 'Polygon', symbol: 'MATIC', explorer: 'https://polygonscan.com' },
};

// Get contract address for a chain
export const getContractAddress = (chainId) => CONTRACT_ADDRESSES[chainId] || null;

// Get explorer URL for a chain
export const getExplorerUrl = (chainId, type, hash) => {
  const chain = SUPPORTED_CHAINS[chainId];
  if (!chain) return '#';
  return `${chain.explorer}/${type}/${hash}`;
};

// V2 Contract ABI with UMA Oracle support
export const CONTRACT_ABI = [
  {
    "type": "constructor",
    "inputs": [{ "name": "_treasury", "type": "address" }],
    "stateMutability": "nonpayable"
  },
  { "type": "receive", "stateMutability": "payable" },
  {
    "type": "function",
    "name": "ASSERTION_LIVENESS",
    "inputs": [],
    "outputs": [{ "name": "", "type": "uint64" }],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "PLATFORM_FEE_BPS",
    "inputs": [],
    "outputs": [{ "name": "", "type": "uint256" }],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "createBet",
    "inputs": [
      { "name": "description", "type": "string" },
      { "name": "oddsBps", "type": "uint256" }
    ],
    "outputs": [{ "name": "", "type": "uint256" }],
    "stateMutability": "payable"
  },
  {
    "type": "function",
    "name": "acceptBet",
    "inputs": [{ "name": "betId", "type": "uint256" }],
    "outputs": [],
    "stateMutability": "payable"
  },
  {
    "type": "function",
    "name": "cancelBet",
    "inputs": [{ "name": "betId", "type": "uint256" }],
    "outputs": [],
    "stateMutability": "nonpayable"
  },
  {
    "type": "function",
    "name": "resolveBetManual",
    "inputs": [
      { "name": "betId", "type": "uint256" },
      { "name": "outcome", "type": "uint8" }
    ],
    "outputs": [],
    "stateMutability": "nonpayable"
  },
  {
    "type": "function",
    "name": "requestOracleResolution",
    "inputs": [
      { "name": "betId", "type": "uint256" },
      { "name": "creatorWins", "type": "bool" }
    ],
    "outputs": [],
    "stateMutability": "nonpayable"
  },
  {
    "type": "function",
    "name": "getBet",
    "inputs": [{ "name": "betId", "type": "uint256" }],
    "outputs": [{
      "name": "",
      "type": "tuple",
      "components": [
        { "name": "id", "type": "uint256" },
        { "name": "creator", "type": "address" },
        { "name": "taker", "type": "address" },
        { "name": "description", "type": "string" },
        { "name": "creatorStake", "type": "uint256" },
        { "name": "takerStake", "type": "uint256" },
        { "name": "oddsBps", "type": "uint256" },
        { "name": "status", "type": "uint8" },
        { "name": "outcome", "type": "uint8" },
        { "name": "createdAt", "type": "uint256" },
        { "name": "resolvedAt", "type": "uint256" },
        { "name": "assertionId", "type": "bytes32" },
        { "name": "creatorWinsAsserted", "type": "bool" }
      ]
    }],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "getUserBets",
    "inputs": [{ "name": "user", "type": "address" }],
    "outputs": [{ "name": "", "type": "uint256[]" }],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "getStats",
    "inputs": [],
    "outputs": [
      { "name": "_totalBets", "type": "uint256" },
      { "name": "_totalVolume", "type": "uint256" },
      { "name": "_totalFees", "type": "uint256" }
    ],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "nextBetId",
    "inputs": [],
    "outputs": [{ "name": "", "type": "uint256" }],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "owner",
    "inputs": [],
    "outputs": [{ "name": "", "type": "address" }],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "treasury",
    "inputs": [],
    "outputs": [{ "name": "", "type": "address" }],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "totalVolume",
    "inputs": [],
    "outputs": [{ "name": "", "type": "uint256" }],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "totalFees",
    "inputs": [],
    "outputs": [{ "name": "", "type": "uint256" }],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "oracleEnabled",
    "inputs": [],
    "outputs": [{ "name": "", "type": "bool" }],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "isOracleEnabled",
    "inputs": [],
    "outputs": [{ "name": "", "type": "bool" }],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "oracle",
    "inputs": [],
    "outputs": [{ "name": "", "type": "address" }],
    "stateMutability": "view"
  },
  {
    "type": "function",
    "name": "bondCurrency",
    "inputs": [],
    "outputs": [{ "name": "", "type": "address" }],
    "stateMutability": "view"
  },
  {
    "type": "event",
    "name": "BetCreated",
    "inputs": [
      { "name": "betId", "type": "uint256", "indexed": true },
      { "name": "creator", "type": "address", "indexed": true },
      { "name": "description", "type": "string", "indexed": false },
      { "name": "creatorStake", "type": "uint256", "indexed": false },
      { "name": "takerStake", "type": "uint256", "indexed": false },
      { "name": "oddsBps", "type": "uint256", "indexed": false }
    ],
    "anonymous": false
  },
  {
    "type": "event",
    "name": "BetAccepted",
    "inputs": [
      { "name": "betId", "type": "uint256", "indexed": true },
      { "name": "taker", "type": "address", "indexed": true },
      { "name": "takerStake", "type": "uint256", "indexed": false }
    ],
    "anonymous": false
  },
  {
    "type": "event",
    "name": "BetResolved",
    "inputs": [
      { "name": "betId", "type": "uint256", "indexed": true },
      { "name": "outcome", "type": "uint8", "indexed": false },
      { "name": "winner", "type": "address", "indexed": false },
      { "name": "payout", "type": "uint256", "indexed": false },
      { "name": "platformFee", "type": "uint256", "indexed": false }
    ],
    "anonymous": false
  },
  {
    "type": "event",
    "name": "BetCancelled",
    "inputs": [{ "name": "betId", "type": "uint256", "indexed": true }],
    "anonymous": false
  },
  {
    "type": "event",
    "name": "ResolutionRequested",
    "inputs": [
      { "name": "betId", "type": "uint256", "indexed": true },
      { "name": "assertionId", "type": "bytes32", "indexed": true },
      { "name": "creatorWinsAsserted", "type": "bool", "indexed": false }
    ],
    "anonymous": false
  },
  {
    "type": "event",
    "name": "OracleEnabled",
    "inputs": [
      { "name": "oracle", "type": "address", "indexed": false },
      { "name": "bondCurrency", "type": "address", "indexed": false }
    ],
    "anonymous": false
  }
];

// Bet status enum (V2 has extra PendingResolution status)
export const BetStatus = {
  Open: 0,
  Active: 1,
  PendingResolution: 2,
  Resolved: 3,
  Cancelled: 4
};

// Outcome enum
export const Outcome = {
  Pending: 0,
  CreatorWins: 1,
  TakerWins: 2,
  Draw: 3,
  Cancelled: 4
};

// Helper to convert odds from basis points to decimal
export const bpsToOdds = (bps) => (Number(bps) / 10000).toFixed(2);

// Helper to convert odds from decimal to basis points
export const oddsToBps = (odds) => Math.round(parseFloat(odds) * 10000);

// Helper to format ETH from wei
export const weiToEth = (wei) => {
  if (!wei) return '0';
  return (Number(wei) / 1e18).toFixed(4);
};

// Helper to convert ETH to wei
export const ethToWei = (eth) => {
  return BigInt(Math.round(parseFloat(eth) * 1e18));
};

// Get status label
export const getStatusLabel = (status) => {
  const labels = {
    0: 'Open',
    1: 'Active',
    2: 'Awaiting Oracle',
    3: 'Resolved',
    4: 'Cancelled'
  };
  return labels[status] || 'Unknown';
};

// Get outcome label
export const getOutcomeLabel = (outcome) => {
  const labels = {
    0: 'Pending',
    1: 'Creator Wins',
    2: 'Taker Wins',
    3: 'Draw',
    4: 'Cancelled'
  };
  return labels[outcome] || 'Unknown';
};
